import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Check, X, AlertCircle } from 'lucide-react';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const SyncStatus: React.FC = () => {
  const {
    isInitialized,
    isSyncing,
    lastSyncTime,
    syncError,
    syncData,
    needsDataSync
  } = useSQLiteSync();

  const handleSync = async () => {
    try {
      await syncData();
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync data');
    }
  };

  const getSyncStatusIcon = () => {
    if (isSyncing) {
      return <RefreshCw className="h-4 w-4 animate-spin" />;
    }
    if (syncError) {
      return <X className="h-4 w-4 text-red-500" />;
    }
    if (lastSyncTime && !needsDataSync) {
      return <Check className="h-4 w-4 text-green-500" />;
    }
    return <AlertCircle className="h-4 w-4 text-yellow-500" />;
  };

  const getSyncStatusColor = () => {
    if (syncError) return 'text-red-500';
    if (!isInitialized) return 'text-gray-500';
    if (needsDataSync) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getSyncStatusText = () => {
    if (!isInitialized) return 'Database not initialized';
    if (isSyncing) return 'Syncing...';
    if (syncError) return 'Sync failed';
    if (!lastSyncTime) return 'Never synced';
    if (needsDataSync) return 'Sync needed';
    return `Last sync: ${format(lastSyncTime, 'HH:mm')}`;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing || !isInitialized}
            className={`gap-2 ${getSyncStatusColor()}`}
          >
            {getSyncStatusIcon()}
            <span className="text-sm">{getSyncStatusText()}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            {syncError && <p className="text-red-500">Error: {syncError}</p>}
            {lastSyncTime && (
              <p>Last synced: {format(lastSyncTime, 'PPpp')}</p>
            )}
            {needsDataSync && <p className="text-yellow-500">Click to sync data</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};